import { Component, CUSTOM_ELEMENTS_SCHEMA, inject } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { SimulationService } from '../../services/simulation.service';
import { isPreprodFullAccess } from '../../../core/config/preprod';

@Component({
  selector: 'app-simulation-banner',
  standalone: true,
  imports: [CommonModule, AsyncPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <ng-container *ngIf="simulationState$ | async as state">
      <div
        class="simulation-banner"
        [class.simulation-banner--write]="state.writeEnabled"
        *ngIf="state.active"
      >
        <div class="simulation-banner__content">
          <i class="fas fa-exclamation-triangle"></i>
          <span class="simulation-banner__text">
            <ng-container *ngIf="!state.writeEnabled">
              <strong>Modo Simulação</strong> - Você está navegando como
              <strong>{{ state.nomeSimulado }}</strong>.
              <ng-container *ngIf="!preprodFullAccess">Nenhuma ação será executada.</ng-container>
              <ng-container *ngIf="preprodFullAccess">
                Ambiente pre-prod: as ações serão efetivadas no banco apartado (dados D-1).
              </ng-container>
            </ng-container>
            <ng-container *ngIf="state.writeEnabled">
              <strong>⚠ ATUAÇÃO TOTAL</strong> — Você está atuando como
              <strong>{{ state.nomeSimulado }}</strong>.
              Todas as ações que você executar serão registradas em nome dele.
            </ng-container>
          </span>
          <button
            class="br-button secondary small simulation-banner__btn"
            type="button"
            (click)="showModal = true"
          >
            <i class="fas fa-sign-out-alt mr-1"></i>
            Encerrar simulação
          </button>
        </div>

        <!-- Modal de confirmação -->
        <div class="simulation-modal-overlay" role="presentation" *ngIf="showModal" (click)="showModal = false">
          <div class="simulation-modal" role="presentation" (click)="$event.stopPropagation()">
            <div class="simulation-modal__header">
              <h4>Encerrar simulação</h4>
            </div>
            <div class="simulation-modal__body">
              <div class="br-message warning mb-3">
                <div class="icon">
                  <i class="fas fa-exclamation-triangle fa-lg"></i>
                </div>
                <div class="content">
                  <span class="message-title">Atenção!</span>
                  <span class="message-body" *ngIf="!state.writeEnabled">
                    Deseja realmente encerrar a simulação e voltar ao seu perfil original?
                    As informações exibidas não serão alteradas.
                  </span>
                  <span class="message-body" *ngIf="state.writeEnabled">
                    Deseja encerrar a atuação como <strong>{{ state.nomeSimulado }}</strong>?
                    As ações já realizadas permanecem registradas em nome dele.
                  </span>
                </div>
              </div>
            </div>
            <div class="simulation-modal__footer">
              <button
                class="br-button secondary"
                type="button"
                (click)="showModal = false"
              >
                Permanecer na Simulação
              </button>
              <button
                class="br-button primary"
                type="button"
                (click)="confirmarEncerramento()"
              >
                Confirmar encerramento
              </button>
            </div>
          </div>
        </div>
      </div>
    </ng-container>
  `,
  styles: [`
    .simulation-banner {
      position: sticky;
      top: 0;
      z-index: 1050;
      background-color: #fff3cd;
      border-bottom: 2px solid #ffc107;
      padding: 8px 16px;
    }

    .simulation-banner--write {
      background-color: #f8d7da;
      border-bottom: 3px solid #dc3545;
      animation: simulation-pulse 2s ease-in-out infinite;
    }

    @keyframes simulation-pulse {
      0%, 100% { background-color: #f8d7da; }
      50%      { background-color: #f1b0b7; }
    }

    .simulation-banner__content {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      max-width: 1200px;
      margin: 0 auto;
      flex-wrap: wrap;
    }

    .simulation-banner__content > i {
      color: #856404;
      font-size: 1.2rem;
    }

    .simulation-banner--write .simulation-banner__content > i {
      color: #721c24;
      font-size: 1.4rem;
    }

    .simulation-banner__text {
      color: #856404;
      font-size: 0.9rem;
    }

    .simulation-banner--write .simulation-banner__text {
      color: #721c24;
      font-size: 0.95rem;
    }

    .simulation-banner__btn {
      white-space: nowrap;
    }

    .simulation-modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: 1060;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .simulation-modal {
      background: white;
      border-radius: 8px;
      padding: 24px;
      max-width: 500px;
      width: 90%;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }

    .simulation-modal__header {
      margin-bottom: 16px;
    }

    .simulation-modal__header h4 {
      margin: 0;
    }

    .simulation-modal__footer {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      margin-top: 16px;
    }
  `],
})
export class SimulationBannerComponent {
  private simulationService = inject(SimulationService);

  simulationState$ = this.simulationService.simulationState$;
  showModal = false;
  readonly preprodFullAccess = isPreprodFullAccess();

  confirmarEncerramento(): void {
    this.showModal = false;
    this.simulationService.encerrarSimulacao();
  }
}
